import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  QrCode, 
  FileText, 
  TrendingUp, 
  CheckCircle,
  Clock,
  ExternalLink,
  Settings,
  Download,
  Mail,
  ChevronRight,
  ChevronLeft,
  Trash2,
  CreditCard,
  X,
  Maximize2,
  Calendar,
  MapPin,
  Edit,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import MessagePopup from '../components/MessagePopup';
import MerkeskytingHomeCard from '../components/MerkeskytingHomeCard';
import MonthlyChampionCard from '../components/MonthlyChampionCard';
import ClubRecordsCard from '../components/ClubRecordsCard';
import { WeaponCardFullscreen } from '../components/MyWeaponCards';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { user, isSuperAdmin, isOrgAdmin, isRangeOfficer, refreshUser } = useAuth();
  const toast = useToast();
  const [stats, setStats] = useState({
    totalSessions: 0,
    thisMonth: 0,
    verified: 0,
    pending: 0
  });
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [showPdfOptions, setShowPdfOptions] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // Messages state
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [myMessages, setMyMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [messagesPage, setMessagesPage] = useState(1);
  const MESSAGES_PER_PAGE = 5;
  
  // Weapon Cards state for preview section
  const [weaponCards, setWeaponCards] = useState([]);
  const [loadingWeaponCards, setLoadingWeaponCards] = useState(true);
  const [fullscreenCard, setFullscreenCard] = useState(null);

  // Function to get branch info from session (for discipline badge)
  const getBranchFromLocation = (session) => {
    // Check location discipline first (more reliable)
    if (session.location_discipline) {
      const key = String(session.location_discipline).toLowerCase();
      const map = {
        nsf:  { name: 'NSF',  color: 'bg-blue-600',    textColor: 'text-white' },
        dfs:  { name: 'DFS',  color: 'bg-purple-500',  textColor: 'text-white' },
        dssn: { name: 'DSSN', color: 'bg-purple-600',  textColor: 'text-white' },
        sws:  { name: 'SWS',  color: 'bg-[#40E0D0]',  textColor: 'text-gray-900' },
        nsu:  { name: 'NSU',  color: 'bg-amber-500',   textColor: 'text-gray-900' },
        nrof: { name: 'NROF', color: 'bg-lime-700',    textColor: 'text-white' },
        nmsf: { name: 'NMSF', color: 'bg-fuchsia-700', textColor: 'text-white' },
        njff: { name: 'NJFF', color: 'bg-green-700',   textColor: 'text-white' },
        nbif: { name: 'NBIF', color: 'bg-sky-600',     textColor: 'text-white' },
        nbsf: { name: 'NBSF', color: 'bg-rose-600',    textColor: 'text-white' },
        nssf: { name: 'NSSF', color: 'bg-cyan-600',    textColor: 'text-white' },
      };
      if (map[key]) return map[key];
    }
    
    // Fallback: check activity name
    const activity = (session.activity || '').toLowerCase();
    if (activity.includes('dssn') || activity.includes('dynamic') || activity.includes('praktisk') || activity.includes('ipsc')) {
      return {
        name: 'DSSN',
        color: 'bg-purple-600',
        textColor: 'text-white'
      };
    }
    
    // Default to NSF
    return {
      name: 'NSF',
      color: 'bg-blue-600',
      textColor: 'text-white'
    };
  };

  // Helper function to merge PDF with selected documents
  const mergePDFWithDocuments = async (pdfBytes, selectedDocUrls) => {
    if (!selectedDocUrls || selectedDocUrls.length === 0) {
      return pdfBytes; // No documents to merge
    }

    try {
      const { PDFDocument } = await import('pdf-lib');
      
      // Load the main PDF
      const mainPdf = await PDFDocument.load(pdfBytes);
      
      // Fetch and merge each selected document
      for (const docUrl of selectedDocUrls) {
        try {
          console.log(`📄 Processing document: ${docUrl}`);
          
          // Determine if we need to use the proxy endpoint
          // Use proxy for CloudFront/S3 URLs to bypass CORS restrictions
          const isCloudFrontOrS3 = docUrl.includes('cloudfront.net') || docUrl.includes('amazonaws.com');
          const isCloudinary = docUrl.includes('cloudinary.com');
          const isExternalUrl = docUrl.startsWith('http://') || docUrl.startsWith('https://');
          
          let fetchUrl = docUrl;
          const fetchOptions = {};
          
          if (isCloudFrontOrS3) {
            // Use backend proxy for CloudFront/S3 URLs to bypass CORS
            fetchUrl = `${API_BASE}/api/documents/proxy?url=${encodeURIComponent(docUrl)}`;
            fetchOptions.headers = {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            };
            console.log(`📄 Using proxy for CloudFront URL: ${fetchUrl}`);
          } else if (isCloudinary) {
            // Cloudinary URLs can be fetched directly (they have CORS enabled)
            // URL-encode the filename part to handle spaces and special characters
            const urlParts = docUrl.split('/');
            const filename = urlParts[urlParts.length - 1];
            const encodedFilename = encodeURIComponent(filename);
            fetchUrl = urlParts.slice(0, -1).join('/') + '/' + encodedFilename;
            console.log(`📄 Fetching Cloudinary document directly: ${fetchUrl}`);
          } else if (!isExternalUrl) {
            // Local file - add Authorization header
            fetchOptions.headers = {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            };
          }
          
          const response = await fetch(fetchUrl, fetchOptions);
          
          if (!response.ok) {
            console.warn(`⚠️ Failed to fetch document: ${fetchUrl} (Status: ${response.status})`);
            continue;
          }
          
          const docBytes = await response.arrayBuffer();
          const contentType = response.headers.get('content-type');
          
          // Check if it's a PDF or image
          if (contentType && contentType.includes('pdf')) {
            // Merge PDF pages
            const docPdf = await PDFDocument.load(docBytes);
            const copiedPages = await mainPdf.copyPages(docPdf, docPdf.getPageIndices());
            copiedPages.forEach(page => mainPdf.addPage(page));
            console.log(`✅ Merged PDF document: ${docUrl}`);
          } else if (contentType && contentType.startsWith('image/')) {
            // Convert image to PDF page
            const page = mainPdf.addPage();
            const { width, height } = page.getSize();
            
            let image;
            if (contentType.includes('png')) {
              image = await mainPdf.embedPng(docBytes);
            } else if (contentType.includes('jpeg') || contentType.includes('jpg')) {
              image = await mainPdf.embedJpg(docBytes);
            } else {
              console.warn(`⚠️ Unsupported image type: ${contentType}`);
              continue;
            }
            
            // Scale image to fit page while maintaining aspect ratio
            const imageAspect = image.width / image.height;
            const pageAspect = width / height;
            
            let drawWidth, drawHeight;
            if (imageAspect > pageAspect) {
              // Image is wider - fit to width
              drawWidth = width * 0.9; // 90% of page width for margins
              drawHeight = drawWidth / imageAspect;
            } else {
              // Image is taller - fit to height
              drawHeight = height * 0.9; // 90% of page height for margins
              drawWidth = drawHeight * imageAspect;
            }
            
            // Center the image on the page
            const x = (width - drawWidth) / 2;
            const y = (height - drawHeight) / 2;
            
            page.drawImage(image, {
              x,
              y,
              width: drawWidth,
              height: drawHeight,
            });
            
            console.log(`✅ Merged image document: ${docUrl}`);
          } else {
            console.warn(`⚠️ Unsupported document type: ${contentType}`);
          }
        } catch (docError) {
          console.error(`❌ Error merging document ${docUrl}:`, docError);
        }
      }
      
      // Save the merged PDF
      const mergedPdfBytes = await mainPdf.save();
      return mergedPdfBytes;
    } catch (error) {
      console.error('❌ Error in mergePDFWithDocuments:', error);
      return pdfBytes; // Return original PDF on error
    }
  };

  // PDF generation functions (copied from TrainingLog.js)
  const downloadStandardPDF = async () => {
    setExportLoading(true);
    try {
      // Get all user's sessions for PDF
      const params = { user_id: user.id };
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/training/sessions`, { params, headers });
      const allSessions = response.data || [];
      
      // IMPORTANT: For police PDF export, ONLY include VERIFIED sessions
      const sessions = allSessions.filter(session => session.verified === true);

      // Import jsPDF dynamically
      const { default: jsPDF } = await import('jspdf');
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Header
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('TRENINGSLOGG - NSF (Trening)', 105, 20, { align: 'center' });

      // Medlemsinformasjon (venstre side)
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Medlem:', 20, 40);
      pdf.setFont('helvetica', 'normal');
      pdf.text(user?.full_name || 'Navn ikke tilgjengelig', 40, 40);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Fødselsdato:', 20, 50);
      pdf.setFont('helvetica', 'normal');
      pdf.text(user?.birth_number || 'Ikke oppgitt', 50, 50);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Medlemsorganisasjon:', 20, 60);
      pdf.setFont('helvetica', 'normal');
      pdf.text('NSF', 70, 60);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Skytterlag/skytterklubb:', 20, 70);
      pdf.setFont('helvetica', 'normal');
      pdf.text(user?.organization?.name || 'Klubb ikke tilgjengelig', 75, 70);

      // Logo placeholder (høyre side)
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(user?.organization?.name?.toUpperCase() || 'PISTOLKLUBB', 150, 40);

      // KRAVSTATUS seksjon - beregn treninger innen siste 6 måneder
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      
      // Filter sessions within last 6 months for NSF requirement
      const sessionsLast6Months = sessions.filter(session => {
        const sessionDate = new Date(session.start_time);
        return sessionDate >= sixMonthsAgo;
      });
      
      const trainingsCount = sessionsLast6Months.length;
      const isRequirementMet = trainingsCount >= 10;
      
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Svart tekst for "KRAVSTATUS:"
      pdf.text('KRAVSTATUS:', 20, 85);
      
      // Sett farge basert på om kravet er oppfylt
      if (isRequirementMet) {
        pdf.setTextColor(0, 128, 0); // Grønn tekst når oppfylt
      } else {
        pdf.setTextColor(220, 53, 69); // Rød tekst når ikke oppfylt
      }
      
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Standard våpensøknad: ${trainingsCount}/10 treninger innen 6 mnd (${isRequirementMet ? 'OPPFYLT' : 'IKKE OPPFYLT'})`, 20, 95);

      // Table header
      let yPosition = 115;
      pdf.setFillColor(255, 215, 0); // Gul bakgrunn
      pdf.rect(20, yPosition - 5, 170, 10, 'F');
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('Dato', 25, yPosition);
      pdf.text('Aktivitet', 65, yPosition);
      pdf.text('Bane/sted', 115, yPosition);
      pdf.text('Verifisert av', 155, yPosition);
      
      yPosition += 15;
      
      // Table rows
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      
      sessions.forEach((session, index) => {
        if (yPosition > 245) { // New page if needed
          pdf.addPage();
          yPosition = 30;
        }
        
        // Alternating row colors
        if (index % 2 === 1) {
          pdf.setFillColor(248, 248, 248);
          pdf.rect(20, yPosition - 5, 170, 10, 'F');
        }
        
        pdf.text(new Date(session.start_time).toLocaleDateString('no-NO'), 25, yPosition);
        pdf.text((session.activity || 'Trening').substring(0, 25), 65, yPosition);
        pdf.text((session.location_name || 'Ukjent').substring(0, 25), 115, yPosition);
        pdf.text((session.verified_by || 'Ikke verifisert').substring(0, 20), 155, yPosition);
        
        yPosition += 8;
      });

      // Signatur seksjon
      yPosition += 20;
      if (yPosition > 240) {
        pdf.addPage();
        yPosition = 30;
      }
      
      pdf.setDrawColor(0, 0, 0);
      pdf.rect(20, yPosition, 170, 40);
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text('SIGNATUR OG STEMPEL', 25, yPosition + 10);

      // Merge with selected documents if any
      const pdfBytes = pdf.output('arraybuffer');
      const finalPdfBytes = await mergePDFWithDocuments(pdfBytes, selectedDocuments);
      
      // Create blob and download
      const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      const orgName = (user?.organization?.name || 'Treningslogg').replace(/[^a-zA-Z0-9æøå]/g, '-');
      link.download = `treningslogg-${orgName}-${today}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      const docMessage = selectedDocuments.length > 0 
        ? ` (med ${selectedDocuments.length} vedlagt${selectedDocuments.length > 1 ? 'e' : ''} dokument${selectedDocuments.length > 1 ? 'er' : ''})`
        : '';
      toast.success(`PDF-treningslogg lastet ned${docMessage}!`);
      
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Kunne ikke generere PDF. Prøv igjen senere.');
    } finally {
      setExportLoading(false);
    }
  };

  const downloadPolicePDF = async () => {
    setExportLoading(true);
    
    try {
      console.log('🚀 Starting backend police PDF generation from Home...');
      
      const response = await axios.post(`${API_BASE}/api/pdf/generate-police-pdf`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });
      
      console.log('✅ Backend PDF generated successfully from Home');
      
      // Merge with selected documents if any
      const pdfBytes = await response.data.arrayBuffer();
      const finalPdfBytes = await mergePDFWithDocuments(pdfBytes, selectedDocuments);
      
      // Create blob URL and trigger download
      const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const today = new Date().toISOString().split('T')[0];
      const userName = user?.full_name?.replace(/\s+/g, '_') || 'medlem';
      link.download = `aktivitetslogg_vapensoknad_${userName}_${today}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      const docMessage = selectedDocuments.length > 0 
        ? ` (med ${selectedDocuments.length} vedlagt${selectedDocuments.length > 1 ? 'e' : ''} dokument${selectedDocuments.length > 1 ? 'er' : ''})`
        : '';
      toast.success(`Politi-godkjent aktivitetslogg lastet ned${docMessage}!`);
      
    } catch (error) {
      console.error('❌ Error generating police PDF from Home:', error);
      
      if (error.response?.status === 401) {
        toast.error('Du må være logget inn for å laste ned PDF');
      } else {
        toast.error(`Kunne ikke generere PDF: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Download PDF function - same advanced layout as TrainingLog.js
  const downloadPDF = async () => {
    try {
      // Get all user's training sessions
      const params = { user_id: user.id };
      const response = await axios.get(`${API_BASE}/api/training/sessions`, { params });
      const allSessions = response.data;
      
      // IMPORTANT: For police PDF export, ONLY include VERIFIED sessions
      const sessions = allSessions.filter(session => session.verified === true);

      if (sessions.length === 0) {
        alert('Du har ingen godkjente treningsøkter å eksportere');
        return;
      }

      // Import jsPDF dynamically
      const { default: jsPDF } = await import('jspdf');
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Header - følger din mal NØYAKTIG
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('TRENINGSLOGG - NSF (Trening)', 105, 20, { align: 'center' });

      // Medlemsinformasjon (venstre side)
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Medlem:', 20, 40);
      pdf.setFont('helvetica', 'normal');
      pdf.text(user?.full_name || 'Navn ikke tilgjengelig', 40, 40);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Fødselsdato:', 20, 50);
      pdf.setFont('helvetica', 'normal');
      pdf.text(user?.birth_number || 'Ikke oppgitt', 50, 50);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Medlemsorganisasjon:', 20, 60);
      pdf.setFont('helvetica', 'normal');
      pdf.text('NSF', 70, 60);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Skytterlag/skytterklubb:', 20, 70);
      pdf.setFont('helvetica', 'normal');
      pdf.text(user?.organization?.name || 'Klubb ikke tilgjengelig', 75, 70);

      // Logo placeholder (høyre side)
      if (user?.organization?.logo_url) {
        pdf.text(user.organization.name, 150, 40);
      } else {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(user?.organization?.name?.toUpperCase() || 'PISTOLKLUBB', 150, 40);
      }

      // KRAVSTATUS seksjon - beregn treninger innen siste 6 måneder
      const sixMonthsAgoAdmin = new Date();
      sixMonthsAgoAdmin.setMonth(sixMonthsAgoAdmin.getMonth() - 6);
      
      // Filter sessions within last 6 months for NSF requirement
      const sessionsLast6MonthsAdmin = sessions.filter(session => {
        const sessionDate = new Date(session.start_time);
        return sessionDate >= sixMonthsAgoAdmin;
      });
      
      const trainingsCountAdmin = sessionsLast6MonthsAdmin.length;
      const isRequirementMetAdmin = trainingsCountAdmin >= 10;
      
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Svart tekst for "KRAVSTATUS:"
      pdf.text('KRAVSTATUS:', 20, 85);
      
      // Sett farge basert på om kravet er oppfylt
      if (isRequirementMetAdmin) {
        pdf.setTextColor(0, 128, 0); // Grønn tekst når oppfylt
      } else {
        pdf.setTextColor(220, 53, 69); // Rød tekst når ikke oppfylt
      }
      
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Standard våpensøknad: ${trainingsCountAdmin}/10 treninger innen 6 mnd (${isRequirementMetAdmin ? 'OPPFYLT' : 'IKKE OPPFYLT'})`, 20, 95);

      // Table header - NØYAKTIG som din mal med GUL bakgrunn
      let yPosition = 115;
      pdf.setFillColor(255, 215, 0); // Gul bakgrunn som i din mal
      pdf.rect(20, yPosition - 5, 170, 10, 'F');
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('Dato', 25, yPosition);
      pdf.text('Aktivitet', 65, yPosition); // Moved right for more space
      pdf.text('Bane/sted', 115, yPosition); // Moved right for more space
      pdf.text('Verifisert av', 155, yPosition);
      
      yPosition += 15;
      
      // Table rows - NØYAKTIG som din mal med alternerende rader
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      
      sessions.forEach((session, index) => {
        // Check for page break
        if (yPosition > 260) {
          pdf.addPage();
          yPosition = 30;
        }

        // Alternating row colors (very light gray for even rows)
        if (index % 2 === 1) {
          pdf.setFillColor(248, 248, 248);
          pdf.rect(20, yPosition - 4, 170, 8, 'F');
        }

        const date = new Date(session.start_time).toLocaleDateString('no-NO');
        const activity = session.activity_type || 'Manual Test Training';
        const location = session.location_name || 'Bane 1 - 25 meter';
        const verified = session.verified_by_name || (session.verified ? 'Admin' : 'Ikke verifisert');

        // More space between columns to prevent overlap
        pdf.text(date, 25, yPosition);
        pdf.text(activity.substring(0, 25), 65, yPosition); // Truncate if too long
        pdf.text(location.substring(0, 25), 115, yPosition); // Truncate if too long  
        pdf.text(verified.substring(0, 20), 155, yPosition); // Truncate if too long

        yPosition += 8;
      });

      // Signatur og stempel seksjon nederst
      const bottomY = 250;
      pdf.setLineWidth(0.5);
      pdf.rect(20, bottomY, 170, 30);
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text('SIGNATUR OG STEMPEL', 25, bottomY + 8);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text('Styremedlem/skyteleders signatur:', 25, bottomY + 16);
      pdf.line(85, bottomY + 16, 140, bottomY + 16);
      
      pdf.text('Dato:', 25, bottomY + 22);
      pdf.line(40, bottomY + 22, 75, bottomY + 22);
      
      pdf.text('Signatur søker:', 25, bottomY + 28);
      pdf.line(60, bottomY + 28, 115, bottomY + 28);

      // STEMPEL placeholder
      pdf.setTextColor(200, 200, 200);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.text('STEMPEL', 150, bottomY + 20);

      // Save PDF
      pdf.save(`treningslogg_${user.full_name.replace(/\s+/g, '_')}.pdf`);
      
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Kunne ikke generere PDF. Prøv igjen senere.');
    }
  };

  // Get visible banners (respekt banner_visible settings)
  // Supports both legacy (banner_urls) and new (settings.bannerUrls) storage locations
  const { banners: visibleBanners, indices: visibleBannerIndices, links: visibleBannerLinks } = useMemo(() => {
    // Get banners from both legacy and new locations
    const legacyBanners = user?.organization?.banner_urls || [];
    const settingsBanners = user?.organization?.settings?.bannerUrls || [];
    
    // Use settings banners if available, otherwise fall back to legacy
    const allBannerUrls = settingsBanners.length > 0 ? settingsBanners : legacyBanners;
    
    // Get visibility arrays from corresponding location
    const legacyVisible = user?.organization?.banner_visible || [];
    const settingsVisible = user?.organization?.settings?.bannerVisible || [];
    const bannerVisible = settingsBanners.length > 0 ? settingsVisible : legacyVisible;
    
    // Get links from corresponding location
    const legacyLinks = user?.organization?.banner_links || [];
    const settingsLinks = user?.organization?.settings?.bannerLinks || [];
    const bannerLinks = settingsBanners.length > 0 ? settingsLinks : legacyLinks;
    
    if (!allBannerUrls.length) return { banners: [], indices: [], links: [] };
    
    const banners = [];
    const indices = [];
    const links = [];
    
    allBannerUrls.forEach((url, index) => {
      // If banner_visible array doesn't exist or index doesn't exist, assume visible (backward compatibility)
      // If banner_visible[index] is explicitly false, hide the banner
      if (bannerVisible?.[index] !== false) {
        banners.push(url);
        indices.push(index);
        links.push(bannerLinks[index] || '');
      }
    });
    
    return { banners, indices, links };
  }, [user?.organization?.banner_urls, user?.organization?.banner_visible, user?.organization?.banner_links, user?.organization?.settings]);

  // Pre-select all documents when PDF dialog opens
  useEffect(() => {
    if (showPdfOptions && user) {
      // Build available documents list (same logic as in render)
      const availableDocs = [];
      
      // Check startkort_url
      if (user.startkort_url) {
        let fullUrl = user.startkort_url;
        if (!fullUrl.startsWith('http')) {
          fullUrl = fullUrl.startsWith('/api') 
            ? `${window.location.origin}${fullUrl}` 
            : `${API_BASE}${fullUrl}`;
        }
        availableDocs.push(fullUrl);
      }
      
      // Check medlemskort_urls
      if (user.medlemskort_urls?.length > 0) {
        user.medlemskort_urls.forEach((url) => {
          let fullUrl = url;
          if (!fullUrl.startsWith('http')) {
            fullUrl = url.startsWith('/api') 
              ? `${window.location.origin}${url}` 
              : `${API_BASE}${url}`;
          }
          availableDocs.push(fullUrl);
        });
      }
      
      // Check weapon_cert_urls
      if (user.weapon_cert_urls?.length > 0) {
        user.weapon_cert_urls.forEach((url) => {
          let fullUrl = url;
          if (!fullUrl.startsWith('http')) {
            fullUrl = url.startsWith('/api') 
              ? `${window.location.origin}${url}` 
              : `${API_BASE}${url}`;
          }
          availableDocs.push(fullUrl);
        });
      }
      
      // Pre-select all documents (default to all selected)
      if (availableDocs.length > 0) {
        setSelectedDocuments(availableDocs);
        console.log('📄 Home: Pre-selected all documents for PDF:', availableDocs.length);
      }
    }
  }, [showPdfOptions, user]);

  // Banner rotation effect (every 15 seconds) - only for visible banners
  useEffect(() => {
    if (visibleBanners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex(prev => 
          (prev + 1) % visibleBanners.length
        );
      }, 15000);
      
      return () => clearInterval(interval);
    }
  }, [visibleBanners.length]);

  const loadDashboardData = async () => {
    try {
      // Load training sessions for current user or organization
      const params = {};
      if (!isSuperAdmin) {
        if (isOrgAdmin || isRangeOfficer) {
          params.organization_id = user.organization_id;
        } else {
          params.user_id = user.id;
        }
      }
      
      const sessionsResponse = await axios.get(`${API_BASE}/api/training/sessions`, { params });
      const sessions = sessionsResponse.data;
      
      // Calculate stats
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const totalSessions = sessions.length;
      const thisMonthSessions = sessions.filter(s => new Date(s.start_time) >= thisMonth).length;
      const verifiedSessions = sessions.filter(s => s.verified).length;
      const pendingSessions = sessions.filter(s => !s.verified).length;
      
      setStats({
        totalSessions,
        thisMonth: thisMonthSessions,
        verified: verifiedSessions,
        pending: pendingSessions
      });
      
      // Get recent sessions (last 5)
      setRecentSessions(sessions.slice(0, 10));
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread messages count and check if popup should be shown
  const fetchUnreadMessages = async () => {
    // Skip if messages module is disabled
    if (user?.organization?.messages_enabled === false) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/messages/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const count = response.data.unread_count || 0;
      setUnreadCount(count);
      
      // Show popup if there are unread messages
      if (count > 0) {
        setShowMessagePopup(true);
      }
    } catch (error) {
      console.error('Failed to fetch unread messages:', error);
    }
  };

  // Fetch all messages for the user (for the messages section)
  const fetchMyMessages = async () => {
    // Skip if messages module is disabled
    if (user?.organization?.messages_enabled === false) return;
    
    setLoadingMessages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/messages/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch my messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Check for unread messages on component mount
  useEffect(() => {
    if (user) {
      fetchUnreadMessages();
      fetchMyMessages();
    }
  }, [user]);

  // Fetch weapon cards for preview section
  const fetchWeaponCards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/my-weapon-cards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeaponCards(response.data.cards || []);
    } catch (error) {
      console.error('Failed to fetch weapon cards:', error);
    } finally {
      setLoadingWeaponCards(false);
    }
  };

  // Fetch weapon cards on mount
  useEffect(() => {
    if (user) {
      fetchWeaponCards();
    }
  }, [user]);

  // Handle message popup close
  const handleCloseMessagePopup = () => {
    setShowMessagePopup(false);
    // Refresh counts and messages
    fetchUnreadMessages();
    fetchMyMessages();
  };

  // Toggle message expansion and mark as read if opening
  const toggleMessageExpanded = async (messageId) => {
    const newSet = new Set(expandedMessages);
    const isExpanding = !newSet.has(messageId);
    
    if (isExpanding) {
      newSet.add(messageId);
      
      // Mark as read when expanding
      const message = myMessages.find(m => m.id === messageId);
      if (message && !message.is_read) {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API_BASE}/api/messages/${messageId}/read`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Update local state to mark as read
          setMyMessages(prev => prev.map(m => 
            m.id === messageId ? { ...m, is_read: true } : m
          ));
          
          // Update unread count
          setUnreadCount(prev => Math.max(0, prev - 1));
          
          // Notify navigation to update badge
          window.dispatchEvent(new CustomEvent('messageRead'));
        } catch (error) {
          console.error('Failed to mark message as read:', error);
        }
      }
    } else {
      newSet.delete(messageId);
    }
    
    setExpandedMessages(newSet);
  };

  // Hide/delete message from user's view
  const handleHideMessage = async (e, messageId) => {
    e.stopPropagation(); // Prevent card expansion
    
    if (!window.confirm('Er du sikker på at du vil slette denne meldingen fra din visning?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/messages/${messageId}/hide`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state
      setMyMessages(prev => prev.filter(m => m.id !== messageId));
      
      // Update unread count if needed
      window.dispatchEvent(new CustomEvent('messageRead'));
      
      toast.success('Meldingen er slettet');
    } catch (error) {
      console.error('Failed to hide message:', error);
      toast.error('Kunne ikke slette meldingen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Message Popup - Only show if messages module is enabled */}
      {user?.organization?.messages_enabled !== false && showMessagePopup && (
        <MessagePopup onClose={handleCloseMessagePopup} />
      )}

      {/* Birth-date reminder banner - shows max 1x per 30 days until user adds birth_number */}
      {user?.birth_reminder_needed && (
        <div
          className="bg-yellow-500/15 border border-yellow-500/50 rounded-lg p-4 flex items-start gap-3"
          data-testid="birth-reminder-banner"
        >
          <div className="text-2xl flex-shrink-0">📅</div>
          <div className="flex-1">
            <h3 className="text-yellow-200 font-semibold text-sm">
              Husk å legge inn fødselsdato i profilen
            </h3>
            <p className="text-yellow-100/90 text-sm mt-1">
              Fødselsdato kreves for korrekt utfylling av Politi-PDF og Standard PDF. Legg inn fødselsdatoen din én gang så slipper du flere påminnelser.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Link
                to="/profile"
                className="inline-flex items-center px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-gray-900 rounded text-sm font-medium"
                data-testid="birth-reminder-go-to-profile"
              >
                Gå til Profil
              </Link>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/snooze-birth-reminder`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (refreshUser) await refreshUser();
                  } catch (e) { /* ignore */ }
                }}
                className="inline-flex items-center px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded text-sm"
                data-testid="birth-reminder-dismiss"
              >
                Påminn meg senere
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Welcome Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-white mb-2">
          Velkommen til {user?.organization?.name || 'Aktivlogg'}
        </h1>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Registrer dine treningstimer enkelt og sikkert. Skann QR-koden på skytebanen for å starte økten.
        </p>
      </div>

      {/* Main QR Scan Section */}
      <div className="max-w-md mx-auto">
        <Link
          to="/scanner"
          className="group block bg-gray-800 hover:bg-gray-700 rounded-lg p-8 text-center transition-colors border-2 border-gray-700 hover:border-yellow-500"
        >
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-500 rounded-xl group-hover:scale-110 transition-transform">
              <QrCode className="w-10 h-10 text-gray-900" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
            Skann QR-kode
          </h2>
          <p className="text-gray-400 text-sm">
            Registrer treningsøkt ved å skanne QR-koden på skytebanen
          </p>
        </Link>
      </div>

      {/* Action Cards Section - Dynamic width based on number of cards */}
      <div className={`grid grid-cols-1 gap-6 ${(isOrgAdmin || isSuperAdmin) ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
        {/* Administrer */}
        {(isOrgAdmin || isSuperAdmin) && (
          <Link to="/admin" className="group">
            <div className="bg-gray-800 hover:bg-gray-700 rounded-lg p-6 transition-colors">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-600 rounded-xl group-hover:scale-110 transition-transform">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-bold text-white mb-2">
                    Administrer
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Administrer medlemmer, treninger og organisasjonsinnstillinger
                  </p>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Min treningslogg */}
        <Link to="/log" className="group">
          <div className="bg-gray-800 hover:bg-gray-700 rounded-lg p-6 transition-colors">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-xl group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold text-white mb-2">
                  Min treningslogg
                </h3>
                <p className="text-gray-400 text-sm">
                  Vis oversikt over dine gjennomførte treningsøkter
                </p>
              </div>
            </div>
          </div>
        </Link>

        {/* Last ned aktivitetslogg (PDF) */}
        <div 
          className="group cursor-pointer"
          onClick={async () => {
            console.log("📄 Home PDF button clicked");
            // Refresh user data to get latest documents
            await refreshUser();
            setShowPdfOptions(true);
          }}
        >
          <div className="bg-gray-800 hover:bg-gray-700 rounded-lg p-6 transition-colors">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-600 rounded-xl group-hover:scale-110 transition-transform">
                  <Download className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold text-white mb-2">
                  Last ned aktivitetslogg (PDF)
                </h3>
                <p className="text-gray-400 text-sm">
                  Eksporter treningsloggen som PDF for våpensøknader
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Terminliste stevner – ekstern lenke til SkytterAdmin */}
        <a
          href="https://app.skyting.no/competitions"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative"
          data-testid="home-terminliste-link"
        >
          <div className="bg-gray-800 hover:bg-gray-700 rounded-lg p-6 transition-colors h-full">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white overflow-hidden group-hover:scale-110 transition-transform">
                  <img
                    src="/assets/nsf-logo.svg"
                    alt="NSF"
                    className="w-12 h-12 object-contain"
                  />
                </div>
              </div>
              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-1.5">
                  Terminliste stevner
                  <svg
                    className="w-4 h-4 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </h3>
                <p className="text-gray-400 text-sm">
                  På SkytterAdmin finner du full stevneliste for påmelding
                </p>
              </div>
            </div>
          </div>
        </a>

        {/* Live stevneresultater InrX – ekstern lenke */}
        <a
          href="https://www.inrx.org/ixl/stevner"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative"
          data-testid="home-inrx-link"
        >
          <div className="bg-gray-800 hover:bg-gray-700 rounded-lg p-6 transition-colors h-full">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white overflow-hidden group-hover:scale-110 transition-transform">
                  <img src="/assets/inrx-logo.png" alt="InrX" className="w-10 h-10 object-contain" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold text-white mb-2">Live stevneresultater InrX</h3>
                <p className="text-gray-400 text-sm">
                  Se live resultater fra alle stevner med en full stevneoversikt.
                </p>
              </div>
            </div>
          </div>
        </a>

        {/* Megalink – Live resultater + resultatlister */}
        <div className="group relative" data-testid="home-megalink-box">
          <div className="bg-gray-800 rounded-lg p-6 transition-colors h-full flex flex-col">
            <div className="flex-1 flex items-center justify-center min-h-[80px]">
              <img
                src="/assets/megalink-logo.png"
                alt="Megalink"
                className="max-h-14 w-auto object-contain bg-white rounded-lg p-2"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href="https://live.megalink.no/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-[110px] px-3 py-2 rounded-md text-white text-sm font-semibold text-center transition-transform active:scale-95 hover:brightness-110"
                style={{ background: '#3DBE62' }}
                data-testid="home-megalink-live-now"
              >
                Live Now
              </a>
              <a
                href="https://res.megalink.no/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-[110px] px-3 py-2 rounded-md text-white text-sm font-semibold text-center transition-transform active:scale-95 hover:brightness-110"
                style={{ background: '#555555' }}
                data-testid="home-megalink-results"
              >
                Result lists
              </a>
            </div>
          </div>
        </div>

        {/* QR Scanner for Weapon Maintenance - Available to all members */}
        <Link to="/weapon-scanner" className={`${(isOrgAdmin || isSuperAdmin) ? 'col-span-1 md:col-span-5' : 'col-span-1 md:col-span-4'}`}>
          <div className="flex items-center justify-center gap-4 w-full p-6 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 rounded-2xl text-gray-900 font-bold text-lg md:text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02]">
            <QrCode className="w-8 h-8 md:w-10 md:h-10" />
            <span>Skann QR for å bidra med puss/vedlikehold av klubbvåpen / egne våpen</span>
          </div>
        </Link>
      </div>

      {/* Sales Banner Section - Kun vis synlige bannere */}
      {visibleBanners.length > 0 && (
        <div className="relative bg-gray-800 rounded-lg overflow-hidden max-w-full">
          {visibleBanners.map((bannerUrl, displayIndex) => {
            // Get the link for this banner from our computed array
            const bannerLink = visibleBannerLinks[displayIndex];
            
            return (
              <div
                key={displayIndex}
                className={`transition-opacity duration-1000 ${
                  displayIndex === currentBannerIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'
                }`}
              >
                {bannerLink ? (
                  <a
                    href={bannerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    <img 
                      src={bannerUrl} 
                      alt={`Sponsor banner ${displayIndex + 1}`}
                      className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-full h-auto mx-auto object-contain group-hover:opacity-90 transition-opacity"
                      style={{ aspectRatio: 'auto' }}
                    />
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                  </a>
                ) : (
                  <img 
                    src={bannerUrl} 
                    alt={`Sponsor banner ${displayIndex + 1}`}
                    className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-full h-auto mx-auto object-contain"
                    style={{ aspectRatio: 'auto' }}
                  />
                )}
              </div>
            );
          })}
          
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <FileText className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Totale økter</p>
              <p className="text-xl font-bold text-white">{stats.totalSessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <TrendingUp className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Denne måneden</p>
              <p className="text-xl font-bold text-white">{stats.thisMonth}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Godkjente</p>
              <p className="text-xl font-bold text-white">{stats.verified}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <Clock className="h-5 w-5 text-orange-400" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Venter godkjenning</p>
              <p className="text-xl font-bold text-white">{stats.pending}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Weapon Cards Preview Section */}
      {weaponCards.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6" data-testid="weapon-cards-preview">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-yellow-400" />
              <h2 className="text-lg font-bold text-white">Mine Våpenkort</h2>
            </div>
            <Link 
              to="/profile" 
              className="text-sm text-yellow-500 hover:text-yellow-400 flex items-center gap-1"
            >
              Administrer <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          
          <p className="text-sm text-gray-400 mb-4">Trykk for å vise i fullskjerm</p>
          
          {/* Cards Grid - Horizontal scroll on mobile */}
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 sm:overflow-visible">
            {weaponCards.filter(c => c.qr_code_url).map((card) => {
              // Calculate status
              let statusColor = 'border-gray-600';
              let expiryText = '';
              let expiryColor = 'text-gray-400';
              let showWarning = false;
              
              if (card.valid_until) {
                const now = new Date();
                const expiry = new Date(card.valid_until);
                const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
                expiryText = expiry.toLocaleDateString('no-NO');
                
                if (daysUntilExpiry < 0) {
                  statusColor = 'border-red-500';
                  expiryColor = 'text-red-400';
                  expiryText = `Utløpt ${expiry.toLocaleDateString('no-NO')}`;
                  showWarning = true;
                } else if (daysUntilExpiry <= 7) {
                  statusColor = 'border-red-500';
                  expiryColor = 'text-red-400';
                  expiryText = `${daysUntilExpiry} dag${daysUntilExpiry !== 1 ? 'er' : ''} igjen`;
                  showWarning = true;
                } else if (daysUntilExpiry <= 30) {
                  statusColor = 'border-yellow-500';
                  expiryColor = 'text-yellow-400';
                  expiryText = `${daysUntilExpiry} dager igjen`;
                  showWarning = true;
                } else {
                  statusColor = 'border-green-500';
                  expiryColor = 'text-green-400';
                }
              }
              
              return (
                <button
                  key={card.id}
                  onClick={() => setFullscreenCard(card)}
                  className={`flex-shrink-0 w-36 sm:w-44 bg-gray-700 rounded-lg p-3 border-2 ${statusColor} hover:bg-gray-600 transition-colors text-left group relative`}
                  data-testid={`weapon-card-preview-${card.id}`}
                >
                  {/* QR Preview */}
                  <div className="aspect-square bg-white rounded mb-3 overflow-hidden">
                    <img 
                      src={card.qr_code_url} 
                      alt="QR"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  {/* Card Info - Larger and more readable */}
                  <p className="text-sm font-bold text-white truncate">
                    {card.weapon_brand}
                  </p>
                  <p className="text-sm text-gray-200 truncate">
                    {card.weapon_model}
                  </p>
                  <p className="text-xs text-gray-300 mt-1">
                    {card.caliber}
                  </p>
                  
                  {/* Expiry Date Display */}
                  {card.valid_until && (
                    <div className={`mt-2 pt-2 border-t border-gray-600 flex items-center gap-1 ${expiryColor}`}>
                      {showWarning && <AlertTriangle className="h-3 w-3 flex-shrink-0" />}
                      <p className="text-xs truncate">
                        {expiryText}
                      </p>
                    </div>
                  )}
                  
                  {/* Tooltip on hover */}
                  {card.valid_until && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 shadow-lg">
                      <div className="font-medium mb-1">QR-kode deling utløper:</div>
                      <div className={expiryColor}>
                        {new Date(card.valid_until).toLocaleDateString('no-NO', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="text-gray-400 mt-1 text-[10px]">Forny via Altinn</div>
                      {/* Tooltip arrow */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Show message if no QR codes uploaded */}
          {weaponCards.filter(c => c.qr_code_url).length === 0 && weaponCards.length > 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              Du har våpenkort uten QR-kode. <Link to="/profile" className="text-yellow-500 hover:text-yellow-400">Gå til profil for å laste opp</Link>
            </p>
          )}
        </div>
      )}

      {/* Fullscreen Weapon Card Modal */}
      {fullscreenCard && (
        <WeaponCardFullscreen 
          card={fullscreenCard} 
          onClose={() => setFullscreenCard(null)} 
        />
      )}

      {/* Merkeskyting-progresjonskort – vises kun for medlemmer som har startet */}
      <MerkeskytingHomeCard />

      {/* Månedens Merkeskytter – vises for alle når det finnes data */}
      <MonthlyChampionCard />

      {/* Klubbrekorder – beste enkeltserier per variant */}
      <ClubRecordsCard />

      {/* Recent Training Sessions */}
      <div className="bg-gray-800 rounded-lg p-4 sm:p-6">
        <h2 className="text-xl font-bold text-white mb-6">Mine siste treningsøkter</h2>
        
        {recentSessions.length === 0 ? (
          <div className="text-center py-8">
            <QrCode className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">Ingen treningsøkter registrert ennå</p>
            <Link
              to="/scanner"
              className="inline-flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
            >
              <QrCode className="h-5 w-5 mr-2" />
              Registrer din første økt
            </Link>
          </div>
        ) : (
          <div>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>Dato</span>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Lokasjon
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Aktivitet
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Godkjent av
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Skytedagbok
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {recentSessions.map((session) => {
                    const branch = getBranchFromLocation(session);
                    return (
                      <tr key={session.id} className="hover:bg-gray-700/50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <div>
                              <div className="text-sm font-medium text-white">
                                {new Date(session.start_time).toLocaleDateString('no-NO')}
                              </div>
                              <div className="text-sm text-gray-400">
                                {new Date(session.start_time).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-white">
                              {session.location_name || 'Ukjent lokasjon'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-white">
                              {session.activity || 'Pistoltrening'}
                            </span>
                            {branch && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${branch.color} ${branch.textColor}`}>
                                {branch.name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            session.verified
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-orange-500/20 text-orange-400'
                          }`}>
                            {session.verified ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Godkjent
                              </>
                            ) : (
                              <>
                                <Clock className="h-3 w-3 mr-1" />
                                Venter
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-400">
                          {session.verified_by || '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <Link
                            to="/log"
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-600 text-gray-300 hover:bg-yellow-500 hover:text-gray-900 border border-gray-500 transition-colors"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Skytedagbok
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {recentSessions.map((session) => {
                const branch = getBranchFromLocation(session);
                return (
                  <div key={session.id} className="bg-gray-700 rounded-lg p-4">
                    {/* Header with date and status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-white">
                            {new Date(session.start_time).toLocaleDateString('no-NO')}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(session.start_time).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        session.verified
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {session.verified ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Godkjent
                          </>
                        ) : (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            Venter
                          </>
                        )}
                      </span>
                    </div>
                    
                    {/* Info grid */}
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Lokasjon</p>
                        <p className="text-gray-300 truncate">{session.location_name || 'Ukjent'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Aktivitet</p>
                        <div className="flex items-center gap-1">
                          <p className="text-gray-300 truncate">{session.activity || 'Pistoltrening'}</p>
                          {branch && (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${branch.color} ${branch.textColor}`}>
                              {branch.name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Godkjent av</p>
                        <p className="text-gray-300">{session.verified_by || '-'}</p>
                      </div>
                    </div>
                    
                    {/* Skytedagbok link */}
                    <div className="pt-2 border-t border-gray-600">
                      <Link
                        to="/log"
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-600 text-gray-300 hover:bg-yellow-500 hover:text-gray-900 border border-gray-500 transition-colors"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Skytedagbok
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center pt-4">
              <Link
                to="/log"
                className="text-yellow-500 hover:text-yellow-400 text-sm font-medium"
              >
                Se alle treningsøkter →
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* PDF Options Modal */}
      {showPdfOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-hidden">
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl mt-2 sm:my-8 max-h-[calc(100vh-1rem)] sm:max-h-[90vh] overflow-y-auto overscroll-contain relative">
            {/* Close button - top right */}
            <button
              onClick={() => {
                setShowPdfOptions(false);
                setSelectedDocuments([]);
              }}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 sm:p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors z-10"
              aria-label="Lukk"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <h3 className="text-lg sm:text-xl font-medium text-white mb-4 sm:mb-6 pr-8">Velg PDF-format for eksport</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Police PDF Option - Default */}
              <div className="relative">
                <button
                  onClick={async () => {
                    console.log("🚔 Police PDF option clicked from Home");
                    setShowPdfOptions(false);
                    await downloadPolicePDF();
                    setSelectedDocuments([]); // Clear after download
                  }}
                  disabled={exportLoading}
                  className="w-full p-3 sm:p-4 bg-yellow-600/20 border-2 border-yellow-500 rounded-lg hover:bg-yellow-600/30 transition-colors relative disabled:opacity-50"
                >
                  <div className="absolute top-2 right-2 bg-yellow-500 text-black text-xs px-2 py-1 rounded font-bold">
                    ANBEFALT
                  </div>
                  
                  {/* Thumbnail placeholder for police form */}
                  <div className="w-full h-24 sm:h-32 bg-white rounded-lg mb-2 sm:mb-3 border flex items-center justify-center">
                    <div className="text-center text-gray-600 text-xs">
                      <div className="font-bold text-sm">POLITIET</div>
                      <div className="mt-1">Aktivitetslogg for våpensøknad</div>
                      <div className="mt-2 text-xs hidden sm:block">
                        <div>□ Organisert trening □ Klubbstevne</div>
                        <div>Sted: _____________</div>
                        <div>Dato: _____________</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-left">
                    <div className="text-white font-medium text-base sm:text-lg">Aktivitetslogg for våpensøknad</div>
                    <div className="text-yellow-300 text-xs sm:text-sm mt-1">Politiets offisielle skjema</div>
                    <div className="text-gray-300 text-xs sm:text-sm mt-2">
                      • Kun trening og klubbstevne aktiviteter
                      • Nøyaktig kopi av politiets PDF-skjema
                      • Kreves for våpenlisenssøknader
                      <span className="hidden sm:inline"> • 24 rader for registreringer</span>
                    </div>
                  </div>
                </button>
              </div>

              {/* Standard PDF Option */}
              <div>
                <button
                  onClick={async () => {
                    console.log("📄 Standard PDF option clicked from Home");
                    setShowPdfOptions(false);
                    await downloadStandardPDF();
                    setSelectedDocuments([]); // Clear after download
                  }}
                  disabled={exportLoading}
                  className="w-full p-3 sm:p-4 bg-gray-700 border-2 border-gray-600 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  {/* Thumbnail placeholder for standard report */}
                  <div className="w-full h-24 sm:h-32 bg-white rounded-lg mb-2 sm:mb-3 border flex items-center justify-center">
                    <div className="text-center text-gray-600 text-xs">
                      <div className="font-bold">TRENINGSLOGG</div>
                      <div className="mt-1">FOR VÅPENLISENSSØKNAD</div>
                      <div className="mt-2 hidden sm:block">
                        <div>=================</div>
                        <div>Dato | Aktivitet | Bane</div>
                        <div>=================</div>
                        <div>01.01.24 | Trening | Bane 1</div>
                        <div>02.01.24 | Stevne | Bane 2</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-left">
                    <div className="text-white font-medium text-base sm:text-lg">Standard Treningslogg</div>
                    <div className="text-gray-400 text-xs sm:text-sm mt-1">Intern oversikt</div>
                    <div className="text-gray-300 text-xs sm:text-sm mt-2">
                      • Alle typer aktiviteter inkludert
                      • Detaljert oversikt med verifisering
                      <span className="hidden sm:inline"> • For intern bruk og dokumentasjon • Fleksibelt format</span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Document selection section - Always show, even if no documents */}
            <div className="mt-6 border-t border-gray-700 pt-6">
              <h4 className="text-lg font-medium text-white mb-3 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-400" />
                Legg til filer fra din PROFIL &gt; Dokumenter (velg hvilke filer du ønsker på slutten i PDF-en)
              </h4>
              {(() => {
                // Combine new documents format with legacy format
                let allDocs = [];
                
                // New format: documents array
                // These may have full URLs with old domains, so we need to fix them too
                if (user?.documents && Array.isArray(user.documents)) {
                  allDocs = user.documents.map(doc => {
                    let docUrl = doc.url;
                    
                    // Keep CloudFront/S3 URLs as-is (they work directly)
                    if (docUrl.includes('cloudfront.net') || docUrl.includes('amazonaws.com')) {
                      return { ...doc, url: docUrl };
                    }
                    
                    // For other URLs, extract pathname if it's an absolute URL
                    if (docUrl.startsWith('http')) {
                      const urlObj = new URL(docUrl);
                      const pathname = urlObj.pathname;
                      
                      // Use current origin for /api paths (K8s ingress routing)
                      docUrl = pathname.startsWith('/api')
                        ? `${window.location.origin}${pathname}`
                        : `${API_BASE}${pathname}`;
                    }
                    
                    return {
                      ...doc,
                      url: docUrl
                    };
                  });
                }
                
                // Legacy format: startkort_url
                if (user?.startkort_url) {
                  let fullUrl = user.startkort_url;
                  
                  // Keep CloudFront/S3 URLs as-is (they work directly)
                  if (!fullUrl.includes('cloudfront.net') && !fullUrl.includes('amazonaws.com')) {
                    // Extract path from URL (handle both relative and absolute URLs)
                    let relativePath = fullUrl;
                    if (relativePath.startsWith('http')) {
                      const urlObj = new URL(relativePath);
                      relativePath = urlObj.pathname;
                    }
                    
                    fullUrl = relativePath.startsWith('/api') 
                      ? `${window.location.origin}${relativePath}`
                      : `${API_BASE}${relativePath}`;
                  }
                  
                  allDocs.push({
                    name: 'Startkort',
                    filename: user.startkort_url.split('/').pop(),
                    url: fullUrl
                  });
                }
                
                // Legacy format: medlemskort_urls
                if (user?.medlemskort_urls && Array.isArray(user.medlemskort_urls)) {
                  user.medlemskort_urls.forEach((url, index) => {
                    let fullUrl = url;
                    
                    // Keep CloudFront/S3 URLs as-is (they work directly)
                    if (!fullUrl.includes('cloudfront.net') && !fullUrl.includes('amazonaws.com')) {
                      let relativePath = url;
                      if (relativePath.startsWith('http')) {
                        const urlObj = new URL(relativePath);
                        relativePath = urlObj.pathname;
                      }
                      
                      fullUrl = relativePath.startsWith('/api') 
                        ? `${window.location.origin}${relativePath}`
                        : `${API_BASE}${relativePath}`;
                    }
                    
                    allDocs.push({
                      name: `Medlemskort ${index + 1}`,
                      filename: url.split('/').pop(),
                      url: fullUrl
                    });
                  });
                }
                
                // Legacy format: weapon_cert_urls  
                if (user?.weapon_cert_urls && Array.isArray(user.weapon_cert_urls)) {
                  user.weapon_cert_urls.forEach((url, index) => {
                    let fullUrl = url;
                    
                    // Keep CloudFront/S3 URLs as-is (they work directly)
                    if (!fullUrl.includes('cloudfront.net') && !fullUrl.includes('amazonaws.com')) {
                      let relativePath = url;
                      if (relativePath.startsWith('http')) {
                        const urlObj = new URL(relativePath);
                        relativePath = urlObj.pathname;
                      }
                      
                      fullUrl = relativePath.startsWith('/api') 
                        ? `${window.location.origin}${relativePath}`
                        : `${API_BASE}${relativePath}`;
                    }
                    
                    allDocs.push({
                      name: `Våpengodkjenning ${index + 1}`,
                      filename: url.split('/').pop(),
                      url: fullUrl
                    });
                  });
                }
                
                const availableDocs = allDocs.filter(doc => 
                  doc.url && (
                    doc.url.toLowerCase().endsWith('.pdf') || 
                    doc.url.toLowerCase().endsWith('.jpg') || 
                    doc.url.toLowerCase().endsWith('.jpeg') || 
                    doc.url.toLowerCase().endsWith('.png')
                  )
                );
                
                if (availableDocs.length === 0) {
                  return (
                    <div className="bg-gray-700/30 rounded-lg p-4 text-center">
                      <p className="text-gray-400 text-sm">
                        Ingen dokumenter tilgjengelig. Last opp dokumenter i PROFIL &gt; Dokumenter først.
                      </p>
                    </div>
                  );
                }
                
                return (
                  <>
                    {/* Select All / Deselect All checkbox */}
                    <div className="mb-3 pb-3 border-b border-gray-600">
                      <label className="flex items-center space-x-3 p-2 hover:bg-gray-600/50 rounded cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedDocuments.length === availableDocs.length && availableDocs.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Select all documents
                              setSelectedDocuments(availableDocs.map(doc => doc.url));
                            } else {
                              // Deselect all documents
                              setSelectedDocuments([]);
                            }
                          }}
                          className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 bg-gray-700 border-gray-600 rounded"
                        />
                        <span className="text-sm font-medium text-yellow-400">
                          {selectedDocuments.length === availableDocs.length && availableDocs.length > 0 
                            ? 'Fjern alle' 
                            : 'Legg til alle'}
                        </span>
                        <span className="text-xs text-gray-500">({availableDocs.length} dokumenter tilgjengelig)</span>
                      </label>
                    </div>
                    
                    <div className="bg-gray-700/50 rounded-lg p-4 max-h-48 overflow-y-auto">
                      <div className="space-y-2">
                        {availableDocs.map((doc, index) => {
                          const isSelected = selectedDocuments.includes(doc.url);
                          // Clean up filename - remove user_id prefix if present
                          let displayFilename = doc.filename;
                          if (displayFilename.includes('_')) {
                            const parts = displayFilename.split('_');
                            if (parts.length > 1) {
                              displayFilename = parts.slice(1).join('_');
                            }
                          }
                          
                          return (
                            <label 
                              key={index}
                              className="flex items-center space-x-3 p-2 hover:bg-gray-600/50 rounded cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDocuments([...selectedDocuments, doc.url]);
                                  } else {
                                    setSelectedDocuments(selectedDocuments.filter(url => url !== doc.url));
                                  }
                                }}
                                className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 bg-gray-700 border-gray-600 rounded"
                              />
                              <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-white truncate">{doc.name}</div>
                                <div className="text-xs text-gray-400 truncate">{displayFilename}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    {selectedDocuments.length > 0 && (
                      <p className="text-sm text-blue-400 mt-2">
                        ✓ {selectedDocuments.length} dokument{selectedDocuments.length > 1 ? 'er' : ''} valgt
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 sm:mt-6 gap-3 sm:gap-0">
              <div className="text-gray-400 text-xs sm:text-sm max-w-xs sm:max-w-md">
                💡 Velg &ldquo;Aktivitetslogg for våpensøknad&rdquo; for offisielle henvendelser til politiet
              </div>
              <button
                onClick={() => {
                  console.log("❌ Home Modal close button clicked");
                  setShowPdfOptions(false);
                  setSelectedDocuments([]); // Clear selections when closing
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors w-full sm:w-auto"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages Section - Only show if messages module is enabled */}
      {user?.organization?.messages_enabled !== false && myMessages.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6" data-testid="messages-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-yellow-500" />
              Meldinger
            </h2>
            {unreadCount > 0 && (
              <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                {unreadCount} ulest{unreadCount > 1 ? 'e' : ''}
              </span>
            )}
          </div>
          
          {loadingMessages ? (
            <div className="text-center py-4 text-gray-400">Laster meldinger...</div>
          ) : (
            <div className="space-y-3">
              {myMessages
                .slice((messagesPage - 1) * MESSAGES_PER_PAGE, messagesPage * MESSAGES_PER_PAGE)
                .map((message) => (
                <div 
                  key={message.id}
                  className={`p-4 rounded-lg border-2 transition-colors overflow-hidden ${
                    message.is_read 
                      ? 'bg-gray-700/30 border-gray-600/50 hover:bg-gray-700/50' 
                      : 'bg-yellow-500/15 border-yellow-500/50 hover:bg-yellow-500/25 shadow-[0_0_10px_rgba(234,179,8,0.15)]'
                  }`}
                  data-testid={`message-${message.id}`}
                >
                  <div 
                    className="flex items-start justify-between gap-3 cursor-pointer"
                    onClick={() => toggleMessageExpanded(message.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {!message.is_read && (
                          <span className="w-2.5 h-2.5 bg-yellow-500 rounded-full flex-shrink-0 animate-pulse" />
                        )}
                        <h3 className={`font-medium truncate ${message.is_read ? 'text-gray-300' : 'text-white font-semibold'}`}>
                          {message.subject}
                        </h3>
                        {!message.is_read && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-yellow-500 text-black font-bold rounded uppercase">Ny</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span>Fra: {message.sender_name}</span>
                        <span>•</span>
                        <span>
                          {new Date(message.created_at).toLocaleDateString('nb-NO', {
                            day: 'numeric',
                            month: 'short'
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => handleHideMessage(e, message.id)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        title="Slett melding"
                        data-testid={`delete-message-${message.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                      <ChevronRight 
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          expandedMessages.has(message.id) ? 'rotate-90' : ''
                        }`} 
                      />
                    </div>
                  </div>
                  
                  {/* Expanded content */}
                  {expandedMessages.has(message.id) && (
                    <div 
                      className="mt-4 pt-4 border-t border-gray-600/50 prose prose-invert max-w-none text-sm text-gray-300 overflow-hidden"
                      style={{ overflowWrap: 'break-word', wordBreak: 'normal' }}
                      dangerouslySetInnerHTML={{ __html: message.content }}
                    />
                  )}
                </div>
              ))}
              
              {/* Pagination controls */}
              {myMessages.length > MESSAGES_PER_PAGE && (
                <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-700">
                  <button
                    onClick={() => setMessagesPage(prev => Math.max(1, prev - 1))}
                    disabled={messagesPage === 1}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      messagesPage === 1
                        ? 'bg-gray-700/30 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                    data-testid="messages-prev-page"
                  >
                    <ChevronLeft size={16} />
                    Forrige
                  </button>
                  
                  <span className="text-sm text-gray-400">
                    Side {messagesPage} av {Math.ceil(myMessages.length / MESSAGES_PER_PAGE)}
                  </span>
                  
                  <button
                    onClick={() => setMessagesPage(prev => Math.min(Math.ceil(myMessages.length / MESSAGES_PER_PAGE), prev + 1))}
                    disabled={messagesPage >= Math.ceil(myMessages.length / MESSAGES_PER_PAGE)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      messagesPage >= Math.ceil(myMessages.length / MESSAGES_PER_PAGE)
                        ? 'bg-gray-700/30 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                    data-testid="messages-next-page"
                  >
                    Neste
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Home;